/**
 * CredentialsSetupCard
 *
 * Setup form for connecting a new UniFi Protect NVR.
 * Collects a friendly name, host/IP, username, and password.
 */

import React, { useState } from 'react';
import { useUnifiProtectStore } from '../unifi-protect-store';

interface CredentialsSetupCardProps {
  onAdded?: () => void;
  onCancel?: () => void;
}

function getErrorMessage(error: unknown): string {
  if (error instanceof Error) {
    return error.message;
  }

  if (typeof error === 'object' && error !== null && 'message' in error) {
    const message = error.message;
    return typeof message === 'string' ? message : 'Connection failed';
  }

  return 'Connection failed';
}

export function CredentialsSetupCard({ onAdded, onCancel }: CredentialsSetupCardProps) {
  const { addNvr } = useUnifiProtectStore();
  const [name, setName] = useState('');
  const [host, setHost] = useState('');
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [isConnecting, setIsConnecting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!name.trim()) { setError('A name for this NVR is required.'); return; }
    if (!host.trim()) { setError('NVR host or IP address is required.'); return; }
    if (!username.trim()) { setError('Username is required.'); return; }
    if (!password.trim()) { setError('Password is required.'); return; }

    setIsConnecting(true);
    try {
      await addNvr(name.trim(), host.trim(), username.trim(), password);
      onAdded?.();
    } catch (err) {
      setError(getErrorMessage(err));
    } finally {
      setIsConnecting(false);
    }
  };

  return (
    <div style={{
      background: 'hsl(var(--card))',
      border: '1px solid hsl(var(--border))',
      borderRadius: 12,
      padding: 32,
      maxWidth: 480,
      margin: '0 auto',
    }}>
      <div style={{ marginBottom: 24 }}>
        <h2 style={{ fontSize: 20, fontWeight: 600, color: 'hsl(var(--foreground))', margin: 0 }}>
          Add UniFi Protect NVR
        </h2>
        <p style={{ color: 'hsl(var(--muted-foreground))', marginTop: 8, marginBottom: 0, fontSize: 14 }}>
          Enter a label and your NVR's local IP address with UniFi OS credentials.
        </p>
      </div>

      {error && (
        <div style={{
          background: 'rgba(239,68,68,0.1)',
          border: '1px solid rgba(239,68,68,0.3)',
          borderRadius: 8,
          padding: '10px 14px',
          marginBottom: 20,
          color: '#f87171',
          fontSize: 14,
        }}>
          {error}
        </div>
      )}

      <form onSubmit={handleSubmit}>
        <div style={{ marginBottom: 16 }}>
          <label style={labelStyle}>NVR Name / Label</label>
          <input
            type="text"
            placeholder="Main Office"
            value={name}
            onChange={e => setName(e.target.value)}
            disabled={isConnecting}
            style={inputStyle}
          />
        </div>

        <div style={{ marginBottom: 16 }}>
          <label style={labelStyle}>NVR Host / IP Address</label>
          <input
            type="text"
            placeholder="192.168.1.100"
            value={host}
            onChange={e => setHost(e.target.value)}
            disabled={isConnecting}
            style={inputStyle}
          />
        </div>

        <div style={{ marginBottom: 16 }}>
          <label style={labelStyle}>Username</label>
          <input
            type="text"
            placeholder="admin"
            value={username}
            onChange={e => setUsername(e.target.value)}
            disabled={isConnecting}
            autoComplete="username"
            style={inputStyle}
          />
        </div>

        <div style={{ marginBottom: 24 }}>
          <label style={labelStyle}>Password</label>
          <input
            type="password"
            placeholder="••••••••"
            value={password}
            onChange={e => setPassword(e.target.value)}
            disabled={isConnecting}
            autoComplete="current-password"
            style={inputStyle}
          />
        </div>

        <div style={{ display: 'flex', gap: 10 }}>
          <button
            type="submit"
            disabled={isConnecting}
            style={{
              flex: 1,
              padding: '10px 20px',
              background: isConnecting ? 'hsl(var(--border))' : 'hsl(var(--primary))',
              color: isConnecting ? 'hsl(var(--muted-foreground))' : '#fff',
              border: 'none',
              borderRadius: 8,
              fontSize: 15,
              fontWeight: 600,
              cursor: isConnecting ? 'not-allowed' : 'pointer',
              transition: 'all 0.15s ease',
            }}
          >
            {isConnecting ? 'Connecting...' : 'Add NVR'}
          </button>

          {onCancel && (
            <button
              type="button"
              onClick={onCancel}
              disabled={isConnecting}
              style={{
                padding: '10px 20px',
                background: 'transparent',
                border: '1px solid hsl(var(--border))',
                borderRadius: 8,
                color: 'hsl(var(--muted-foreground))',
                fontSize: 15,
                fontWeight: 500,
                cursor: 'pointer',
              }}
            >
              Cancel
            </button>
          )}
        </div>
      </form>

      <p style={{ marginTop: 16, fontSize: 12, color: 'hsl(var(--muted-foreground))', textAlign: 'center' }}>
        Credentials are encrypted and stored securely. The NVR must be reachable from this server.
      </p>
    </div>
  );
}

const labelStyle: React.CSSProperties = {
  display: 'block', fontSize: 13, fontWeight: 500,
  color: 'hsl(var(--muted-foreground))', marginBottom: 6,
};

const inputStyle: React.CSSProperties = {
  width: '100%',
  padding: '9px 12px',
  background: 'hsl(var(--background))',
  border: '1px solid hsl(var(--border))',
  borderRadius: 8,
  color: 'hsl(var(--foreground))',
  fontSize: 14,
  outline: 'none',
  boxSizing: 'border-box',
};
